import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiFileText, FiBriefcase, FiHeart, FiHome, FiTruck, FiDollarSign, FiMail, FiMessageSquare, FiCheck } = FiIcons;

const RequestServicePage = () => {
  const [formData, setFormData] = useState({
    legalIssue: '',
    country: '',
    city: '',
    budget: '',
    contactMethod: 'email',
    email: '',
    phone: '',
    description: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const legalIssueTypes = [
    { value: 'consultation', label: 'Legal Consultation', icon: FiFileText },
    { value: 'contracts', label: 'Contract Review/Drafting', icon: FiFileText },
    { value: 'business', label: 'Business Formation', icon: FiBriefcase },
    { value: 'family', label: 'Family Law', icon: FiHeart },
    { value: 'real-estate', label: 'Real Estate', icon: FiHome },
    { value: 'employment', label: 'Employment Law', icon: FiTruck },
    { value: 'other', label: 'Other Legal Matter', icon: FiFileText }
  ];

  const budgetRanges = [
    { value: '0-500', label: '$0 - $500' },
    { value: '500-1000', label: '$500 - $1,000' },
    { value: '1000-2500', label: '$1,000 - $2,500' },
    { value: '2500-5000', label: '$2,500 - $5,000' },
    { value: '5000+', label: '$5,000+' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // AI matching logic would go here
    console.log('Form submitted:', formData);
    
    // Simulate AI processing
    setTimeout(() => {
      setIsSubmitted(true);
    }, 1000);
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 py-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white p-8 rounded-lg shadow-lg text-center"
          >
            <div className="bg-green-100 text-green-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiCheck} className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Request Submitted Successfully!
            </h2>
            <p className="text-gray-600 mb-6">
              Our AI has matched you with 3 qualified lawyers. You'll receive their discounted offers within 24 hours.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <p className="text-blue-800 font-medium">
                Expected savings: 30-45% off standard rates
              </p>
            </div>
            <p className="text-sm text-gray-500">
              Check your {formData.contactMethod === 'email' ? 'email' : 'WhatsApp'} for lawyer responses.
            </p>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Get Matched with the Right Lawyer
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Tell us about your legal needs and we'll connect you with verified lawyers offering exclusive discounts
          </p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-8 rounded-lg shadow-lg"
        >
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Legal Issue Type */}
            <div>
              <label className="block text-lg font-semibold text-gray-900 mb-4">
                What type of legal help do you need? *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {legalIssueTypes.map((type) => (
                  <label
                    key={type.value}
                    className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all ${
                      formData.legalIssue === type.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="legalIssue"
                      value={type.value}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <SafeIcon icon={type.icon} className="w-5 h-5 text-blue-600 mr-3" />
                    <span className="font-medium text-gray-900">{type.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Location */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-lg font-semibold text-gray-900 mb-2">
                  Country *
                </label>
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Country</option>
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="UK">United Kingdom</option>
                  <option value="AU">Australia</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-lg font-semibold text-gray-900 mb-2">
                  City *
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your city"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Budget */}
            <div>
              <label className="block text-lg font-semibold text-gray-900 mb-4">
                What's your budget range? *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {budgetRanges.map((range) => (
                  <label
                    key={range.value}
                    className={`flex items-center justify-center p-4 border rounded-lg cursor-pointer transition-all ${
                      formData.budget === range.value
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-green-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="budget"
                      value={range.value}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <SafeIcon icon={FiDollarSign} className="w-5 h-5 text-green-600 mr-2" />
                    <span className="font-medium text-gray-900">{range.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Contact Method */}
            <div>
              <label className="block text-lg font-semibold text-gray-900 mb-4">
                How would you like lawyers to contact you? *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label
                  className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all ${
                    formData.contactMethod === 'email'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="contactMethod"
                    value="email"
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <SafeIcon icon={FiMail} className="w-5 h-5 text-blue-600 mr-3" />
                  <span className="font-medium text-gray-900">Email</span>
                </label>
                <label
                  className={`flex items-center p-4 border rounded-lg cursor-pointer transition-all ${
                    formData.contactMethod === 'whatsapp'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="contactMethod"
                    value="whatsapp"
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <SafeIcon icon={FiMessageSquare} className="w-5 h-5 text-blue-600 mr-3" />
                  <span className="font-medium text-gray-900">WhatsApp</span>
                </label>
              </div>
            </div>

            {/* Contact Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-lg font-semibold text-gray-900 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              {formData.contactMethod === 'whatsapp' && (
                <div>
                  <label className="block text-lg font-semibold text-gray-900 mb-2">
                    WhatsApp Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+1 (555) 123-4567"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-lg font-semibold text-gray-900 mb-2">
                Brief description of your legal matter (optional)
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={4}
                placeholder="Provide any additional details that might help us match you with the right lawyer..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Submit Button */}
            <div className="text-center">
              <button
                type="submit"
                className="btn-primary text-white px-12 py-4 rounded-lg font-semibold text-lg w-full md:w-auto"
              >
                Get Matched with Lawyers
              </button>
              <p className="text-sm text-gray-500 mt-4">
                Free to submit • Get responses within 24 hours • Save 30-50% on legal fees
              </p>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default RequestServicePage;